import { useState, useMemo, useRef, useEffect } from 'react';
import PersonalInfoSection from './components/PersonalInfoSection';
import ProjectInfoSection from './components/ProjectInfoSection';
import SuccessMessage from './components/SuccessMessage';
import ErrorMessage from './components/ErrorMessage';
import Stage2Page from './components/Stage2Page';
import generatePdf from './utils/generatePdf';

export const API_BASE = 'https://octopus.no-ms.dk';

function tokenFromUrl() {
  return new URLSearchParams(window.location.search).get('token');
}

// The link used to BE the customer's data: a JWT whose payload was the whole
// project row, which this app read with jwtDecode. It was signed with the
// backend's database key, so the data was readable by anyone the link reached
// and the key could never be rotated. The link now carries a project number, a
// stage and an expiry, and the details are asked for -- step 2.4 of the platform
// migration. Links issued before that are still accepted by this endpoint, so
// nothing outstanding breaks.
async function fetchLinkContext(token) {
  const response = await fetch(`${API_BASE}/pre-project-form/context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    const error = new Error('link_rejected');
    error.status = response.status;
    try {
      error.detail = (await response.json()).detail;
    } catch (e) {
      // A non-JSON body means the server is unreachable or broken; the generic
      // message is the right one either way.
    }
    throw error;
  }
  const context = await response.json();
  if (context.project_number === undefined || context.project_number === null) {
    throw new Error('project_number_missing');
  }
  return {
    ...context,
    fullName: context.customer_name || '',
    email: context.mail || '',
    address: context.address || '',
    page: context.page || 'stage_1',
  };
}




// Combined initial form data for both personal and project info
const initialFormData = {
  // Personal Info Section
  customer_name: '',
  address: '',
  mail: '',
  phone: '',
  is_company: 'false',
  home_address: '',
  cvr_number: '',
  same_contact_person: 'true',
  overwritten_name: '',
  overwritten_mail: '',
  overwritten_phone: '',
  
  // Project Info Section - TBD fields
  projectType: '',
  title: '',
  description: '',
  startMonth: '',
  
  // Building Information
  constructionType: '',
  fundaments_plan_approval: '',
  ext_measurement_method: '',
  ext_measurement_method_fixed: '',
  ext_measurement_method_link_approved: '',
  distance_build_to_center: 15,
  
  // Vægt og Belastning
  total_weight_kg: '',
  lastNedfoeringsplan_approval: '',
  
  // Kotehøjde
  screw_height_from_ground: '',
  koteplan_approval: '',
  skraaninger: '',
  
  // Jordbundsforhold
  jordbundsanalyse_done: '',
  jordbundsanalyse_none: '',
  jordbundsanalyse_osbl: '',
  jordforhold_comment: '',
  jordforhold_kendskab: '',
  
  // Tilkøb checkboxes
  tilkoeb_gravedag: 'false',
  tilkoeb_proevegravning: 'false',
  tilkoeb_proevegravning_details: '',
  tilkoeb_ler_soegning: 'false',
  tilkoeb_find_ledning: 'false',
  tilkoeb_ingenioer_beregninger: 'false',
  tilkoeb_jordbundsanalyse: 'false',
  tilkoeb_landmaaler_afsaetning: 'false',
  tilkoeb_punkt_afsaetning: 'false',
  tilkoeb_byggetilladelse: 'false',
  
  // Link fields from token
  link_fundamentsplan: '',
  link_measurement_method_plan: '',
  link_lastnedfoeringsplan: '',
  link_koteplan: '',
  link_jordbundsanalyse: '',
};

// Function to populate form data from the link context
function populateFromContext(initialData, context) {
  if (!context) return initialData;
  
  const populatedData = { ...initialData };
  
  // Auto-populate any field where the key matches between context and form data
  Object.keys(context).forEach(key => {
    if (populatedData.hasOwnProperty(key)) {
      populatedData[key] = context[key];
    }
  });
  
  // Handle boolean conversion for is_company
  if (context.is_company !== undefined) {
    populatedData.is_company = context.is_company ? 'true' : 'false';
  }
  
  return populatedData;
}

function App() {
  const token = useMemo(tokenFromUrl, []);
  const formRef = useRef();

  // The link context arrives over the network now, so there are three states to
  // hold rather than one: still asking, answered, refused.
  const [decoded, setDecoded] = useState(null);
  const [error, setError] = useState(null);
  const [resolvingLink, setResolvingLink] = useState(Boolean(token));

  // Unified form data state
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    fetchLinkContext(token)
      .then(context => {
        if (cancelled) return;
        setDecoded(context);
        setFormData(current => populateFromContext(current, context));
      })
      .catch(e => {
        if (!cancelled) setError(e.message || 'link_rejected');
      })
      .finally(() => {
        if (!cancelled) setResolvingLink(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};

    // Personal Info Validation
    if (!formData.customer_name) newErrors.customer_name = 'Påkrævet';

    // Address validation with regex for Danish format
    if (!formData.address) {
      newErrors.address = 'Påkrævet';
    } else if (!/^.+,\s*\d{4}\s+.+$/.test(formData.address)) {
      newErrors.address = 'Adresse skal være i formatet: Vejnavn nummer, postnummer by (f.eks. Tilfældigvej 123, 2500 Valby)';
    }

    // Email validation
    if (!formData.mail) {
      newErrors.mail = 'Påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.mail)) {
      newErrors.mail = 'Ugyldig email format';
    }

    // Danish phone number validation (8 digits)
    if (!formData.phone) {
      newErrors.phone = 'Påkrævet';
    } else if (!/^\d{8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Telefonnummer skal være 8 cifre';
    }

    // Company/Private validation
    if (!formData.is_company) newErrors.is_company = 'Påkrævet';

    // Conditional validation for company/private
    if (formData.is_company === 'false' && !formData.home_address) {
      newErrors.home_address = 'Påkrævet';
    } else if (formData.is_company === 'false' && formData.home_address && !/^.+,\s*\d{4}\s+.+$/.test(formData.home_address)) {
      newErrors.home_address = 'Adresse skal være i formatet: Vejnavn nummer, postnummer by';
    }

    if (formData.is_company === 'true') {
      if (!formData.cvr_number) {
        newErrors.cvr_number = 'Påkrævet';
      } else if (!/^\d{8}$/.test(formData.cvr_number)) {
        newErrors.cvr_number = 'CVR-nummer skal være præcis 8 cifre';
      }
    }

    // Contact person override validation
    if (formData.same_contact_person !== 'true') {
      if (!formData.overwritten_name) newErrors.overwritten_name = 'Påkrævet';

      if (!formData.overwritten_mail) {
        newErrors.overwritten_mail = 'Påkrævet';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.overwritten_mail)) {
        newErrors.overwritten_mail = 'Ugyldig email format';
      }

      if (!formData.overwritten_phone) {
        newErrors.overwritten_phone = 'Påkrævet';
      } else if (!/^\d{8}$/.test(formData.overwritten_phone.replace(/\s/g, ''))) {
        newErrors.overwritten_phone = 'Telefonnummer skal være 8 cifre';
      }
    }
    
    // Project Info Validation
    if (!formData.projectType) newErrors.projectType = 'Påkrævet';
    if (!formData.startMonth) newErrors.startMonth = 'Påkrævet';
    if (!formData.title) newErrors.title = 'Påkrævet';
    if (!formData.description) newErrors.description = 'Påkrævet';
    if (!formData.constructionType) newErrors.constructionType = 'Påkrævet';
    if (!formData.fundaments_plan_approval) newErrors.fundaments_plan_approval = 'Påkrævet';
    if (!formData.ext_measurement_method) newErrors.ext_measurement_method = 'Påkrævet';
    if (!formData.total_weight_kg || formData.total_weight_kg < 1) newErrors.total_weight_kg = 'Påkrævet og skal være mindst 1 kg';
    if (!formData.screw_height_from_ground || formData.screw_height_from_ground < 1) newErrors.screw_height_from_ground = 'Påkrævet og skal være mindst 1 cm';
    if (!formData.lastNedfoeringsplan_approval) newErrors.lastNedfoeringsplan_approval = 'Påkrævet';
    if (!formData.koteplan_approval) newErrors.koteplan_approval = 'Påkrævet';
    if (!formData.skraaninger || formData.skraaninger < 1) newErrors.skraaninger = 'Påkrævet og skal være mindst 1 cm';
    if (formData.jordbundsanalyse_none === 'true' && (!formData.jordbundsanalyse_osbl || formData.jordbundsanalyse_osbl < 1)) newErrors.jordbundsanalyse_osbl = 'Påkrævet og skal være mindst 1 cm';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSubmitError('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      const pdfBlob = await generatePdf(formRef.current);
      const formDataPayload = new FormData();
      formDataPayload.append('pdf', pdfBlob, 'form.pdf');
      formDataPayload.append('jsonData', JSON.stringify({
        ...formData,
        token // include the original JWT from the URL
      }));


      const res = await fetch('https://octopus.no-ms.dk/pre-project-form/submit-form', {
        // const res = await fetch('http://localhost:8000/pre-project-form/submit-form', {
        method: 'POST',
        body: formDataPayload,
      });
      if (!res.ok) {
        let errorMsg = 'Serverfejl ved indsendelse';
        try {
          const errorData = await res.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // If response is not JSON, keep default errorMsg
        }
        throw new Error(errorMsg);
      }
      setSuccess(true);
    } catch (err) {
      setSubmitError(err.message || 'Uventet fejl');
    } finally {
      setLoading(false);
    }
  };

  if (resolvingLink) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary-light px-2 py-8">
        <div className="w-full max-w-xl bg-white p-8 rounded-2xl shadow-xl text-center">
          <p className="text-gray-700">Henter dine oplysninger...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary-light px-2 py-8">
        <div className="w-full max-w-xl bg-white p-8 rounded-2xl shadow-xl text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Something Went Wrong</h1>
          <p className="text-gray-700">
            Dit link kunne ikke bruges. Det er sandsynligvis udl&oslash;bet. Kontakt os,
            s&aring; sender vi et nyt.
          </p>
        </div>
      </div>
    );
  }

  if (decoded?.page === 'stage_2') {
    return (
      <Stage2Page
        formData={formData}
        token={token}
      />
    );
  }

  if (success) return <SuccessMessage />;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-primary-light px-2 py-8">
      <div className="w-full max-w-xl bg-white p-0 rounded-2xl shadow-xl overflow-hidden relative">
        {/* Logo/Header */}
        <div className="flex flex-col items-center justify-center bg-primary-light pt-8 pb-4 px-6 border-b border-gray-100">
          <img
            src={process.env.PUBLIC_URL + '/logo-octopus.png'}
            alt="Normann Olsen Skru fundament logo"
            className="h-20 mb-2 drop-shadow-md"
            style={{ objectFit: 'contain' }}
          />
          <h1 className="text-2xl md:text-3xl font-extrabold text-brand-black mb-1 text-center">Forhåndsformular</h1>
          <p className="text-base text-brand-orange font-bold text-center">Normann Olsen Skruefundament</p>
        </div>
        <form
          ref={formRef}
          className="w-full px-6 py-8 md:px-10 md:py-10 flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <PersonalInfoSection
            values={formData}
            onChange={handleFormDataChange}
            errors={errors}
            isLocked={!!token}
          />
          <div className="my-4 border-t border-gray-200" />
          <ProjectInfoSection
            values={formData}
            onChange={handleFormDataChange}
            errors={errors}
          />
          {submitError && <ErrorMessage message={submitError} />}
          <button
            type="submit"
            className="mt-6 w-full bg-brand-orange text-white font-extrabold rounded-form py-3 text-lg shadow-sm transition hover:bg-orange-500 focus:ring-4 focus:ring-brand-orange/30 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Sender...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default App;
